/**
 * In-app replacement for the browser's alert/confirm/prompt popups, which show the site address
 * ("belivah.com says") and can't be styled. Rendered by <AppDialogHost /> at the app root.
 */
export type DialogKind = 'alert' | 'confirm' | 'prompt';

export type DialogRequest = {
  id: number;
  kind: DialogKind;
  message: string;
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  defaultValue?: string;
  resolve: (value: boolean | string | null) => void;
};

export type DialogOptions = { title?: string; confirmLabel?: string; cancelLabel?: string; danger?: boolean };

let seq = 0;
let queue: DialogRequest[] = [];
const listeners = new Set<(q: DialogRequest[]) => void>();
const emit = () => listeners.forEach((fn) => fn(queue));

export function subscribeDialogs(fn: (q: DialogRequest[]) => void): () => void {
  listeners.add(fn);
  fn(queue);
  return () => { listeners.delete(fn); };
}

export function resolveDialog(id: number, value: boolean | string | null) {
  const req = queue.find((d) => d.id === id);
  queue = queue.filter((d) => d.id !== id);
  emit();
  req?.resolve(value);
}

const DANGER_RE = /^\s*(admin:\s*)?(delete|remove|permanently|ban|block|unfriend|cancel your)/i;

function open(kind: DialogKind, message: unknown, opts: DialogOptions & { defaultValue?: string } = {}) {
  return new Promise<boolean | string | null>((resolve) => {
    const text = String(message ?? '');
    queue = [...queue, {
      id: ++seq,
      kind,
      message: text,
      ...opts,
      danger: opts.danger ?? (kind === 'confirm' && DANGER_RE.test(text)),
      resolve,
    }];
    emit();
  });
}

export function appAlert(message: unknown, opts?: DialogOptions): Promise<void> {
  return open('alert', message, opts).then(() => undefined);
}

export function appConfirm(message: unknown, opts?: DialogOptions): Promise<boolean> {
  return open('confirm', message, opts).then((v) => v === true);
}

export function appPrompt(message: unknown, defaultValue = '', opts?: DialogOptions): Promise<string | null> {
  return open('prompt', message, { ...opts, defaultValue }).then((v) => (typeof v === 'string' ? v : null));
}

/** Routes every existing `alert(...)` call through the in-app dialog. */
export function installAlertOverride() {
  if (typeof window === 'undefined') return;
  window.alert = (message?: unknown) => { void appAlert(message); };
}
